import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    EyeOutlined,
    SafetyOutlined,
} from '@ant-design/icons';
import { MdClose } from 'react-icons/md';
import { FIELD_LABEL, FIELD_ERROR, REQUIRED_MARK, fieldControl, fieldTextarea } from './formStyles';
import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import Loader from '../../components/Loader/Loader';
import CommonPagination from '../../components/CommonPagination/CommonPagination';
import '../../components/Popup/Popup.scss';
import {
    RoleDTO,
    CreateRoleRequest,
    UpdateRoleRequest,
    RoleFormValues,
    ResponseModel
} from './types';

interface RolesManagementProps {
    roles: RoleDTO[];
    onRoleChange: () => void;
    loading: boolean;
}

// @@ AI-COMMENT: Rows per page for the client-side roles pager
const ROLES_PAGE_SIZE = 10;

// @@ AI-COMMENT: Blank form state shared by the create and edit dialogs
const EMPTY_ROLE_FORM: RoleFormValues = { roleName: '', roleDescription: '' };

// @@ AI-COMMENT: Matches the max length the antd Form used to enforce
const ROLE_DESCRIPTION_MAX = 255;

const RolesManagement: React.FC<RolesManagementProps> = ({
    roles,
    onRoleChange,
    loading
}) => {
    // Get logged-in user from authStore
    const { user } = useSelector((s: any) => s.authStore);
    // @@ AI-COMMENT: Modal state management
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [selectedRole, setSelectedRole] = useState<RoleDTO | null>(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    // @@ AI-COMMENT: Controlled form state and validation errors, replacing the antd Form instances
    const [roleForm, setRoleForm] = useState<RoleFormValues>(EMPTY_ROLE_FORM);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    // @@ AI-COMMENT: Mirrors the validation rules the antd Form used to enforce
    const validateRoleForm = () => {
        const errors: Record<string, string> = {};
        const name = roleForm.roleName.trim();
        if (!name) {
            errors.roleName = 'Please enter role name';
        } else if (name.length < 3) {
            errors.roleName = 'Role name must be at least 3 characters';
        } else if (name.length > 50) {
            errors.roleName = 'Role name cannot exceed 50 characters';
        }
        if (roleForm.roleDescription && roleForm.roleDescription.length > ROLE_DESCRIPTION_MAX) {
            errors.roleDescription = `Description cannot exceed ${ROLE_DESCRIPTION_MAX} characters`;
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const closeCreateModal = () => {
        setCreateModalVisible(false);
        setRoleForm(EMPTY_ROLE_FORM);
        setFormErrors({});
    };

    const closeEditModal = () => {
        setEditModalVisible(false);
        setSelectedRole(null);
        setRoleForm(EMPTY_ROLE_FORM);
        setFormErrors({});
    };

    // @@ AI-COMMENT: Submit guards run validation before hitting the create/update handlers
    const submitCreateRole = () => {
        if (validateRoleForm()) handleCreateRole(roleForm);
    };

    const submitUpdateRole = () => {
        if (validateRoleForm()) handleUpdateRole(roleForm);
    };

    // @@ AI-COMMENT: Create role
    const handleCreateRole = async (values: RoleFormValues) => {
        setModalLoading(true);
        try {
            const payload: CreateRoleRequest = {
                roleName: values.roleName,
                roleDescription: values.roleDescription || undefined
            };

            const response = await axiosInstance.post<ResponseModel<RoleDTO>>(
                apis.USER_MANAGEMENT_CREATE_ROLE,
                payload,
                {
                    headers: {
                        userId: user?.id?.toString() || ''
                    }
                }
            );

            if (response.data?.status) {
                message.success('Role created successfully');
                closeCreateModal();
                onRoleChange(); // Refresh roles list
            } else {
                message.error(response.data?.statusDescription || 'Failed to create role');
            }
        } catch (error: any) {
            console.error('Error creating role:', error);
            message.error(error?.response?.data?.statusDescription || 'Failed to create role');
        } finally {
            setModalLoading(false);
        }
    };

    // @@ AI-COMMENT: Update role
    const handleUpdateRole = async (values: RoleFormValues) => {
        if (!selectedRole) return;

        setModalLoading(true);
        try {
            const payload: UpdateRoleRequest = {
                roleId: selectedRole.id,
                roleName: values.roleName !== selectedRole.roleName ? values.roleName : undefined,
                roleDescription: values.roleDescription !== selectedRole.roleDescription ? values.roleDescription : undefined
            };

            const response = await axiosInstance.post<ResponseModel<RoleDTO>>(
                apis.USER_MANAGEMENT_UPDATE_ROLE,
                payload,
                {
                    headers: {
                        userId: user?.id?.toString() || ''
                    }
                }
            );

            if (response.data?.status) {
                message.success('Role updated successfully');
                closeEditModal();
                onRoleChange(); // Refresh roles list
            } else {
                message.error(response.data?.statusDescription || 'Failed to update role');
            }
        } catch (error: any) {
            console.error('Error updating role:', error);
            message.error(error?.response?.data?.statusDescription || 'Failed to update role');
        } finally {
            setModalLoading(false);
        }
    };



    // @@ AI-COMMENT: Modal handlers
    const handleViewRole = (role: RoleDTO) => {
        setSelectedRole(role);
        setViewModalVisible(true);
    };

    const handleEditRole = (role: RoleDTO) => {
        setSelectedRole(role);
        setRoleForm({
            roleName: role.roleName,
            roleDescription: role.roleDescription
        });
        setFormErrors({});
        setEditModalVisible(true);
    };

    // @@ AI-COMMENT: Roles arrive in full from the parent, so paginate client-side for the shared CMS pager
    const visibleRoles = roles.slice((currentPage - 1) * ROLES_PAGE_SIZE, currentPage * ROLES_PAGE_SIZE);

    return (
        <>
            {/* @@ AI-COMMENT: Roles table using the shared CMS table chrome (dark head, zebra rows) */}
            <div className="mt-2.5 campaign-list bg-white mb-[54px]">
                {/* @@ AI-COMMENT: Create action sits above the table; the record count comes from CommonPagination */}
                <div className="px-4 py-2.5 flex items-center justify-end border-b border-gray-200">
                    <button
                        onClick={() => setCreateModalVisible(true)}
                        className="h-8 px-4 rounded text-xs font-semibold bg-blue-500 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 flex items-center gap-1.5"
                    >
                        <PlusOutlined />
                        Add Role
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                        <thead className="bg-[#343434]">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[30%]">Role</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[60%]">Description</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-white w-[10%]">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={3} className="px-4 py-8 text-center text-xs text-gray-500">
                                        <Loader />
                                    </td>
                                </tr>
                            ) : visibleRoles.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="px-4 py-8 text-center text-xs text-gray-500">
                                        No roles found.
                                    </td>
                                </tr>
                            ) : (
                                visibleRoles.map(record => (
                                    <tr
                                        key={record.id}
                                        className="[&:nth-child(odd)]:bg-[#f7f7f7] hover:bg-[#eef4fb] cursor-pointer"
                                        onClick={() => handleViewRole(record)}
                                    >
                                        <td className="px-4 py-3 text-left text-xs text-[#3F3F3F]">
                                            <span className="flex items-center gap-2">
                                                <span className="w-8 h-8 rounded-full bg-[#FEF3C7] flex items-center justify-center flex-shrink-0">
                                                    <SafetyOutlined style={{ color: '#f59e0b', fontSize: 14 }} />
                                                </span>
                                                <span className="flex flex-col">
                                                    <span className="font-semibold">{record.roleName}</span>
                                                    <span className="text-[10px] text-gray-500">Role ID: #{record.id}</span>
                                                </span>
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-left text-xs text-[#3F3F3F]" title={record.roleDescription}>
                                            {record.roleDescription
                                                ? (record.roleDescription.length > 90
                                                    ? `${record.roleDescription.substring(0, 90)}...`
                                                    : record.roleDescription)
                                                : <span className="text-[11px] text-gray-400">No description</span>}
                                        </td>
                                        <td className="px-4 py-3 text-center text-xs">
                                            {/* @@ AI-COMMENT: Explicit view/edit buttons replace the overflow menu, matching the Users tab */}
                                            <span className="inline-flex items-center justify-center gap-1">
                                                <button
                                                    title="View details"
                                                    onClick={e => { e.stopPropagation(); handleViewRole(record); }}
                                                    className="p-1.5 rounded-sm text-green-600 hover:bg-green-50"
                                                >
                                                    <EyeOutlined />
                                                </button>
                                                <button
                                                    title="Edit role"
                                                    onClick={e => { e.stopPropagation(); handleEditRole(record); }}
                                                    className="p-1.5 rounded-sm text-blue-600 hover:bg-blue-50"
                                                >
                                                    <EditOutlined />
                                                </button>
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* @@ AI-COMMENT: Shared CMS pagination replaces the antd pager for consistency across listings */}
            {roles.length > 0 && (
                <CommonPagination
                    data={{ totalCount: roles.length }}
                    PAGE_SIZE={ROLES_PAGE_SIZE}
                    onPageChange={(page: number) => setCurrentPage(page)}
                />
            )}

            {/* @@ AI-COMMENT: Create / Edit Role dialog using the shared CMS popup shell */}
            {(createModalVisible || editModalVisible) && (
                <div className="popup-container">
                    <div className="overlay">
                        <div className="popup-content popup-card popup-card-wide">
                            <div className="popup-head flex items-start justify-between">
                                <div>
                                    <h2>{createModalVisible ? 'Create New Role' : 'Edit Role'}</h2>
                                    <p>{createModalVisible ? 'Fill in the below details.' : 'Update the details for this role.'}</p>
                                </div>
                                <button
                                    onClick={createModalVisible ? closeCreateModal : closeEditModal}
                                    title="Close"
                                    className="popup-close flex items-center justify-center"
                                >
                                    <MdClose size={18} />
                                </button>
                            </div>

                            <div className="popup-body space-y-4">
                                <div>
                                    <label className={FIELD_LABEL}>Role Name <span className={REQUIRED_MARK}>*</span></label>
                                    <input
                                        type="text"
                                        placeholder="Enter role name"
                                        maxLength={50}
                                        className={fieldControl(Boolean(formErrors.roleName))}
                                        value={roleForm.roleName}
                                        onChange={e => setRoleForm(prev => ({ ...prev, roleName: e.target.value }))}
                                    />
                                    {formErrors.roleName
                                        ? <p className={FIELD_ERROR}>{formErrors.roleName}</p>
                                        : <p className="text-[10px] text-[#959595] mt-1">3 to 50 characters.</p>}
                                </div>
                                <div>
                                    <label className={FIELD_LABEL}>Description</label>
                                    <textarea
                                        rows={3}
                                        maxLength={ROLE_DESCRIPTION_MAX}
                                        placeholder="Enter role description (optional)"
                                        className={fieldTextarea(Boolean(formErrors.roleDescription))}
                                        value={roleForm.roleDescription || ''}
                                        onChange={e => setRoleForm(prev => ({ ...prev, roleDescription: e.target.value }))}
                                    />
                                    <div className="flex items-start justify-between gap-2">
                                        {formErrors.roleDescription
                                            ? <p className={FIELD_ERROR}>{formErrors.roleDescription}</p>
                                            : <span />}
                                        {/* @@ AI-COMMENT: Character counter replaces the antd TextArea showCount */}
                                        <p className="text-[10px] text-[#959595] mt-1 whitespace-nowrap ml-auto">
                                            {(roleForm.roleDescription || '').length} / {ROLE_DESCRIPTION_MAX}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="popup-foot bottom-btn-cta flex justify-end items-center">
                                <button
                                    onClick={createModalVisible ? closeCreateModal : closeEditModal}
                                    className="bg-transparent font-semibold text-slate-800 py-2 px-6"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={createModalVisible ? submitCreateRole : submitUpdateRole}
                                    disabled={modalLoading}
                                    className="bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 py-2 px-6"
                                >
                                    {modalLoading
                                        ? 'Saving...'
                                        : createModalVisible ? 'Create Role' : 'Update Role'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* @@ AI-COMMENT: View Role dialog using the shared CMS popup shell */}
            {viewModalVisible && selectedRole && (
                <div className="popup-container">
                    <div className="overlay">
                        <div className="popup-content popup-card popup-card-wide">
                            <div className="popup-head flex items-start justify-between">
                                <div>
                                    <h2>Role Details</h2>
                                    <p>Read-only view of this role.</p>
                                </div>
                                <button
                                    onClick={() => { setViewModalVisible(false); setSelectedRole(null); }}
                                    title="Close"
                                    className="popup-close flex items-center justify-center"
                                >
                                    <MdClose size={18} />
                                </button>
                            </div>

                            <div className="popup-body">
                                <div className="flex items-center gap-4 pb-4 border-b border-[#ededed]">
                                    <span className="w-12 h-12 rounded-full bg-[#FEF3C7] flex items-center justify-center flex-shrink-0">
                                        <SafetyOutlined style={{ color: '#f59e0b', fontSize: 20 }} />
                                    </span>
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-[#43425D] truncate">{selectedRole.roleName}</p>
                                        <p className="text-xs text-[#959595]">Role ID: #{selectedRole.id}</p>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <p className="text-[10px] font-bold text-[#303030] mb-[5px]">Description</p>
                                    <p className={`text-xs p-3 rounded border border-[#e4e4e8] bg-[#f2f2f5] ${selectedRole.roleDescription ? 'text-[#3F3F3F]' : 'text-gray-400 italic'}`}>
                                        {selectedRole.roleDescription || 'No description provided'}
                                    </p>
                                </div>
                            </div>

                            <div className="popup-foot bottom-btn-cta flex justify-end items-center">
                                <button
                                    onClick={() => { setViewModalVisible(false); setSelectedRole(null); }}
                                    className="bg-transparent font-semibold text-slate-800 py-2 px-6"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={() => { setViewModalVisible(false); handleEditRole(selectedRole); }}
                                    className="bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 py-2 px-6"
                                >
                                    Edit Role
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default RolesManagement;
